import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import adminApi from '../../api/adminClient';
import './Admin.css';

export default function AdminNotices() {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState('전체');

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    try {
      setLoading(true);
      const data = await adminApi.get('/notices');
      setNotices(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(n) {
    if (!window.confirm(`"${n.title}" 공지사항을 삭제하시겠습니까?`)) return;
    try {
      await adminApi.delete(`/notices/${n.id}`);
      loadAll();
    } catch (err) {
      alert(err.message);
    }
  }

  const filtered = filter === '필독' ? notices.filter(n => n.isPinned) : notices;

  if (editing !== null) {
    return (
      <AdminLayout>
        <NoticeForm
          initial={editing}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); loadAll(); }}
        />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-header">
        <div>
          <h1>공지사항 관리</h1>
          <p className="text-muted">홈페이지 고객센터 탭에 표시되는 공지사항을 관리합니다.</p>
        </div>
        <button className="btn btn-primary" onClick={() => setEditing({})}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          공지 등록
        </button>
      </div>

      <div className="admin-filter-row">
        {['전체', '필독'].map(f => (
          <button
            key={f}
            className={`type-filter__btn ${filter === f ? 'type-filter__btn--active' : ''}`}
            onClick={() => setFilter(f)}
          >
            {f}{f === '필독' ? ` (${notices.filter(n => n.isPinned).length})` : ''}
          </button>
        ))}
      </div>

      {loading && <div className="state-message">불러오는 중입니다...</div>}
      {!loading && error && <div className="state-message state-message--error">{error}</div>}

      {!loading && !error && (
        filtered.length === 0 ? (
          <div className="state-message">등록된 공지사항이 없습니다.</div>
        ) : (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th style={{ width: 70 }}>필독</th>
                  <th>제목</th>
                  <th style={{ width: 140 }}>등록일</th>
                  <th style={{ width: 160 }}>관리</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(n => (
                  <tr key={n.id}>
                    <td>
                      {n.isPinned && <span className="badge badge-navy">필독</span>}
                    </td>
                    <td className="admin-table__title">{n.title}</td>
                    <td>{String(n.created_at).slice(0, 10)}</td>
                    <td className="admin-table__actions">
                      <button className="admin-action-btn" onClick={() => setEditing(n)}>수정</button>
                      <button className="admin-action-btn admin-action-btn--danger" onClick={() => handleDelete(n)}>삭제</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </AdminLayout>
  );
}

function NoticeForm({ initial, onCancel, onSaved }) {
  const isEdit = !!initial.id;
  const [form, setForm] = useState({
    title: initial.title || '',
    content: initial.content || '',
    isPinned: initial.isPinned || false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) {
      setError('제목과 내용은 필수입니다.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await adminApi.put(`/notices/${initial.id}`, form);
      } else {
        await adminApi.post('/notices', form);
      }
      onSaved();
    } catch (err) {
      setError(err.message || '저장 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <button className="back-btn" onClick={onCancel}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
        목록으로
      </button>
      <div className="admin-form-card">
        <h2>{isEdit ? '공지사항 수정' : '공지사항 등록'}</h2>
        <form onSubmit={handleSubmit}>
          {error && <div className="form-error">{error}</div>}
          <div className="form-group">
            <label>제목 <span className="required">*</span></label>
            <input name="title" value={form.title} onChange={handleChange} placeholder="공지사항 제목을 입력하세요" />
          </div>
          <div className="form-group">
            <label>내용 <span className="required">*</span></label>
            <textarea name="content" value={form.content} onChange={handleChange} rows={8} placeholder="공지사항 내용을 입력하세요." />
          </div>
          <label className="checkbox-row">
            <input type="checkbox" name="isPinned" checked={form.isPinned} onChange={handleChange} style={{ width: 'auto' }} />
            <span>필독 공지로 등록 (목록 상단에 고정됩니다)</span>
          </label>
          <div className="admin-form-actions">
            <button type="button" className="btn btn-outline-navy" onClick={onCancel}>취소</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? '저장 중...' : isEdit ? '수정 완료' : '등록하기'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
